import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { first } from 'rxjs';
import { IGridState, IBaseSearchRequest, PageSize, IGridResult, IFilter } from 'src/app/core/models/base';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { dueDateClass } from 'src/app/shared/helpers/date-helpers';
import { IAssignment, ISearchAssignmentsRequest, TaskStatues, UNASSIGNED_SENTINEL } from '../models/assignment';
import { IAssignmentType } from '../models/assignment-type';
import { IEmployee } from 'src/app/employee/employee';
import { AssignmentService } from '../services/assignment.service';
import { AssignmentTypeService } from '../services/assignment-type.service';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { ContactService } from 'src/app/contact/services/contact.service';
import { AssignmentStorageService } from '../storage/assignment-storage.service';
import { UserSettingsStoreService } from 'src/app/user-settings/services/user-settings-store.service';
import { IAssignmentColumnPref, ISavedAssignmentFilter } from 'src/app/user-settings/models/user-settings';
import { chevronDownIcon, reorderIcon } from '@progress/kendo-svg-icons';
import { buildPagingQueryParams, buildSortQueryParams, pageFromSkip, parsePagingQuery, parseSortQuery } from 'src/app/shared/helpers/grid-query-params';
import { ListQueryStateService } from 'src/app/shared/services/list-query-state.service';
import {
  ASSIGNMENT_SORTABLE_FIELDS,
  ASSIGNMENTS_DEFAULT_SORT,
  buildAssignmentsSearchRequest,
  hasAssignmentFilterParams,
  sortToOrderBy,
} from './assignments-query';

const ALL_ASSIGNMENT_COLUMNS: { field: string; label: string }[] = [
  { field: 'title', label: 'Titel' },
  { field: 'internalNumber', label: 'Interne Nummer' },
  { field: 'dueDate', label: 'Fälligkeit' },
  { field: 'createdOn', label: 'Erstellungsdatum' },
  { field: 'client', label: 'Kunde' },
  { field: 'owner', label: 'Verantwortliche Person' },
  { field: 'assignmentType', label: 'Auftragstyp' },
  { field: 'taskStatus', label: 'Status' },
  { field: 'executors', label: 'Ausführende Personen' },
  { field: 'executionLocation', label: 'Auftragsort' },
];

@UntilDestroy()
@Component({
    selector: 'app-assignments',
    templateUrl: './assignments.component.html',
    styleUrls: ['./assignments.component.css'],
    standalone: false
})
export class AssignmentsComponent implements OnInit {

  constructor(
    private assignmentService : AssignmentService,
    private assignmentTypeService: AssignmentTypeService,
    private employeeService: EmployeeService,
    private contactService: ContactService,
    private route : ActivatedRoute,
    private assignmentStore: AssignmentStorageService,
    private userSettingsStore: UserSettingsStoreService,
    private router: Router,
    private listQueryState: ListQueryStateService
  ) { }

  assignments: IGridResult<IAssignment[]>;

  assignmentTypes: IAssignmentType[] = [];
  executorOptions: Array<{ id: string, firstName: string, lastName: string }> = [];

  // Column visibility & ordering
  orderedColumns: { field: string; label: string }[] = [...ALL_ASSIGNMENT_COLUMNS];
  visibleColumnFields: string[] = ALL_ASSIGNMENT_COLUMNS.map(c => c.field);

  // Column picker popup
  columnPickerOpen = false;
  columnPickerIcon = chevronDownIcon;
  dragHandleIcon = reorderIcon;
  private dragIndex: number | null = null;
  dragOverIndex: number | null = null;

  @ViewChild('columnPickerAnchor', { static: false })
  columnPickerAnchor: ElementRef | undefined;

  @ViewChild('columnPickerPopup', { static: false })
  columnPickerPopup: ElementRef | undefined;

  // Saved filters
  savedFilters: ISavedAssignmentFilter[] = [];

  // Tracks whether we've already auto-applied the persisted last-applied
  // filter once after login — prevents re-apply when the subject re-emits
  // after subsequent store updates.
  private autoAppliedLastFilter = false;

  // Save-dialog state
  saveFilterDialogOpened = false;
  newFilterName = '';

  // Delete-confirmation-dialog state
  deleteFilterDialogOpened = false;
  private filterIdToDelete: string | null = null;

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  // Overwritten in ngOnInit from the URL query params.
  searchRequest: ISearchAssignmentsRequest = {
    pageNumber: 1,
    pageSize: 25,
    orderBy:['dueDate asc'],
    taskStatuses:[TaskStatues.open, TaskStatues.inProgress, TaskStatues.review]
  }

  get pageSizes() {
    return PageSize;
  }

  get TaskStatuses(){
    return TaskStatues;
  }

  public sort: SortDescriptor[] = [{ ...ASSIGNMENTS_DEFAULT_SORT }];

  filterForm = new UntypedFormGroup({
    keyword: new UntypedFormControl(null),
    contact: new UntypedFormControl(null),
    status: new UntypedFormControl([TaskStatues.open, TaskStatues.inProgress, TaskStatues.review]),
    hasNoSchedulerItems: new UntypedFormControl(false),
    assignmentTypeIds: new UntypedFormControl([]),
    executorIds: new UntypedFormControl([]),
  });

  showResetButton = false;

  taskStatusesArray = enumToArray(TaskStatues, "assignment.taskStatuses.");

  defaultItem = {
    id: null,
    name: 'Bitte wählen'
  }

  ngOnInit(): void {
    this.assignments = this.route.snapshot.data['assignments'] as IGridResult<IAssignment[]>;

    // Load dropdown data sources
    this.assignmentTypeService.searchAssignmentTypes({ pageNumber: 0, pageSize: 999999 })
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => {
        this.assignmentTypes = response.data.filter(t => t.isActive);
      });

    this.employeeService.listEmployees({ pageNumber: 0, pageSize: 999999 })
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => {
        this.executorOptions = [
          { id: UNASSIGNED_SENTINEL, firstName: 'Nicht', lastName: 'zugewiesen' },
          ...response.data.map((e: IEmployee) => ({ id: e.id, firstName: e.firstName, lastName: e.lastName }))
        ];
      });

    // Paging, sort and filter come from the URL query params. The resolver has
    // already fetched data using the same query params (same builder), so we
    // don't need to re-fetch here — just make grid and form reflect what's active.
    const q = this.route.snapshot.queryParamMap;
    const paging = parsePagingQuery(q);
    this.gridState = { skip: paging.skip, pageSize: paging.pageSize };
    this.sort = parseSortQuery(q, ASSIGNMENTS_DEFAULT_SORT, ASSIGNMENT_SORTABLE_FIELDS);
    this.searchRequest = buildAssignmentsSearchRequest(q);

    const hadFilter = this.hydrateFilterFromUrl();
    if (hadFilter) {
      this.showResetButton = true;
    }

    this.listQueryState.remember('assignments', this.route.snapshot.queryParams);

    // Subscribe to user settings for saved filters + column visibility.
    this.userSettingsStore.userSettings$
      .pipe(untilDestroyed(this))
      .subscribe(settings => {
        if (!settings) return;
        this.savedFilters = settings.assignmentFilters ?? [];

        const prefs = settings.assignmentColumns ?? [];
        if (prefs.length === 0) {
          this.orderedColumns = [...ALL_ASSIGNMENT_COLUMNS];
          this.visibleColumnFields = ALL_ASSIGNMENT_COLUMNS.map(c => c.field);
        } else {
          // Restore column order from prefs; append any new columns at the end.
          const ordered: { field: string; label: string }[] = [];
          for (const p of prefs) {
            const col = ALL_ASSIGNMENT_COLUMNS.find(c => c.field === p.field);
            if (col) ordered.push(col);
          }
          for (const col of ALL_ASSIGNMENT_COLUMNS) {
            if (!prefs.some(p => p.field === col.field)) ordered.push(col);
          }
          this.orderedColumns = ordered;

          const visibleSet = new Set(prefs.filter(p => p.visible).map(p => p.field));
          this.visibleColumnFields = ordered.map(c => c.field).filter(f => visibleSet.has(f));
        }

        // Auto-apply the persisted last-applied filter on first load after
        // login, but only when the URL carries no state at all (URL wins so
        // direct links and the back navigation from the detail view keep
        // their filter, paging and sort).
        if (!this.autoAppliedLastFilter && settings.lastAppliedAssignmentFilterId) {
          this.autoAppliedLastFilter = true;
          const hasUrlQuery = this.route.snapshot.queryParamMap.keys.length > 0;
          if (!hasUrlQuery) {
            const last = this.savedFilters.find(f => f.id === settings.lastAppliedAssignmentFilterId);
            if (last) {
              this.applySavedFilter(last, /* persist */ false);
            }
          }
        }
      });
  }

  // --- Column visibility & ordering ---

  isColumnVisible(field: string): boolean {
    return this.visibleColumnFields.includes(field);
  }

  private columnTitleKeys: { [field: string]: string } = {
    title: 'assignmentTitle',
    internalNumber: 'internalNumber',
    dueDate: 'dueDate',
    createdOn: 'createdOn',
    client: 'client',
    owner: 'owner',
    assignmentType: 'assignmentType',
    taskStatus: 'taskStatus',
    executors: 'executors',
    executionLocation: 'executionLocation',
  };

  getColumnTitle(field: string, t: (key: string) => string): string {
    return t(this.columnTitleKeys[field] ?? field);
  }

  toggleColumnPicker(event: Event) {
    event.stopPropagation();
    this.columnPickerOpen = !this.columnPickerOpen;
  }

  toggleColumn(field: string, visible: boolean) {
    const nextSet = new Set(this.visibleColumnFields);
    if (visible) nextSet.add(field); else nextSet.delete(field);
    this.visibleColumnFields = this.orderedColumns.map(c => c.field).filter(f => nextSet.has(f));
    this.persistColumns();
  }

  onPointerDown(index: number, event: PointerEvent): void {
    if (event.button !== undefined && event.button !== 0) return;
    this.dragIndex = index;
    this.dragOverIndex = index;
    const target = event.currentTarget as Element;
    target.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onPointerMove(event: PointerEvent): void {
    if (this.dragIndex === null) return;
    event.preventDefault();
    const el = document.elementFromPoint(event.clientX, event.clientY);
    const item = el?.closest('[data-col-idx]') as HTMLElement | null;
    if (!item) return;
    const idx = Number(item.dataset['colIdx']);
    if (!Number.isNaN(idx)) this.dragOverIndex = idx;
  }

  onPointerUp(_event: PointerEvent): void {
    if (
      this.dragIndex !== null &&
      this.dragOverIndex !== null &&
      this.dragIndex !== this.dragOverIndex
    ) {
      const moved = this.orderedColumns.splice(this.dragIndex, 1)[0];
      this.orderedColumns.splice(this.dragOverIndex, 0, moved);
      this.persistColumns();
    }
    this.dragIndex = null;
    this.dragOverIndex = null;
  }

  private persistColumns(): void {
    const prefs: IAssignmentColumnPref[] = this.orderedColumns.map(c => ({
      field: c.field,
      visible: this.visibleColumnFields.includes(c.field),
    }));
    this.userSettingsStore.updateAssignmentColumns(prefs);
  }

  getExecutorsDisplay(executors?: IEmployee[]): string {
    if (!executors?.length) return '';
    return executors.map(e => ((e.firstName ?? '') + ' ' + (e.lastName ?? '')).trim()).join(', ');
  }

  getExecutionLocationDisplay(loc?: any): string {
    if (!loc) return '';
    const name = loc.isCompany
      ? loc.companyName
      : ((loc.firstName ?? '') + ' ' + (loc.lastName ?? '')).trim();
    const parts = [loc.street, ((loc.zip ?? '') + ' ' + (loc.city ?? '')).trim()].filter((v: string) => v);
    return parts.length ? name + ', ' + parts.join(', ') : name;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.columnPickerOpen) return;
    const target = event.target as Node;
    const insidePopup = this.columnPickerPopup?.nativeElement?.contains(target);
    const onAnchor = this.columnPickerAnchor?.nativeElement?.contains(target);
    if (!insidePopup && !onAnchor) {
      this.columnPickerOpen = false;
    }
  }

  // --- Saved filters ---

  applySavedFilter(filter: ISavedAssignmentFilter, persist = true) {
    // Clear form first so removed fields don't leak across.
    this.filterForm.reset();
    this.filterForm.patchValue({
      status: [],
      assignmentTypeIds: [],
      executorIds: [],
    });

    const p = filter.queryParams ?? {};
    const patch: any = {};
    if (p.keyword) patch.keyword = p.keyword;
    if (p.contactId) {
      this.contactService.getContact(p.contactId)
        .pipe(first(), untilDestroyed(this))
        .subscribe(contact => this.filterForm.patchValue({ contact }));
    }
    if (p.status) patch.status = p.status.split(',').map(n => +n);
    if (p.hasNoSchedulerItems) patch.hasNoSchedulerItems = p.hasNoSchedulerItems === '1';
    if (p.assignmentTypeIds) patch.assignmentTypeIds = p.assignmentTypeIds.split(',');
    if (p.executorIds) patch.executorIds = p.executorIds.split(',');

    this.filterForm.patchValue(patch);
    this.syncSearchRequestFromForm();
    this.resetPaging();
    this.writeUrl();
    this.showResetButton = true;
    this.refreshGrid();

    // Persist which saved filter was last applied so it can be restored on
    // next login. Skipped when we're restoring that same pointer ourselves.
    if (persist) {
      this.userSettingsStore.setLastAppliedAssignmentFilter(filter.id);
    }
  }

  openSaveFilterDialog() {
    this.newFilterName = '';
    this.saveFilterDialogOpened = true;
  }

  closeSaveFilterDialog(confirmed: boolean) {
    if (confirmed && this.newFilterName?.trim()) {
      const queryParams = this.buildQueryParamsFromForm();
      const filter: ISavedAssignmentFilter = {
        id: this.generateId(),
        name: this.newFilterName.trim(),
        queryParams: this.stringifyParams(queryParams),
      };
      this.userSettingsStore.saveAssignmentFilter(filter);
    }
    this.saveFilterDialogOpened = false;
    this.newFilterName = '';
  }

  deleteSavedFilter(id: string, event: Event) {
    event.stopPropagation();
    this.filterIdToDelete = id;
    this.deleteFilterDialogOpened = true;
  }

  closeDeleteFilterDialog(confirmed: boolean) {
    if (confirmed && this.filterIdToDelete) {
      this.userSettingsStore.deleteAssignmentFilter(this.filterIdToDelete);
    }
    this.deleteFilterDialogOpened = false;
    this.filterIdToDelete = null;
  }

  private generateId(): string {
    // Lightweight client-side id; no need to be cryptographically strong.
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  private stringifyParams(qp: Params): { [key: string]: string } {
    const out: { [key: string]: string } = {};
    Object.keys(qp).forEach(k => {
      const v = qp[k];
      if (v != null) out[k] = String(v);
    });
    return out;
  }

  /**
   * True when the currently applied filter (form values) matches at least one
   * of the already-saved filters — used to disable the "save filter" button.
   */
  isCurrentFilterSaved(): boolean {
    const current = this.stringifyParams(this.buildQueryParamsFromForm());
    return this.savedFilters.some(f => this.paramsEqual(current, f.queryParams ?? {}));
  }

  /**
   * True if a given saved filter matches the currently applied form state.
   * Used to highlight the active saved-filter button.
   */
  isSavedFilterActive(filter: ISavedAssignmentFilter): boolean {
    const current = this.stringifyParams(this.buildQueryParamsFromForm());
    return this.paramsEqual(current, filter.queryParams ?? {});
  }

  private paramsEqual(a: { [k: string]: string }, b: { [k: string]: string }): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(k => a[k] === b[k]);
  }



  refreshGrid(){
    this.assignmentService.searchAssignments(this.searchRequest)
    .pipe(
      first(),
      untilDestroyed(this))
    .subscribe(response => {
      this.assignments.data = response.data;
      this.assignments.total = response.totalCount;
    })
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: 0, take: pageSize });
  }

  onGridReload() {
    this.refreshGrid();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = pageFromSkip(skip, take);
    this.searchRequest.pageSize = take;

    this.writeUrl();
    this.refreshGrid();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.searchRequest.orderBy = sortToOrderBy(sort);
    this.resetPaging();
    this.writeUrl();
    this.refreshGrid();
  }

  private resetPaging(): void {
    this.gridState.skip = 0;
    this.searchRequest.pageNumber = 1;
  }

  onSubmit(){
    this.showResetButton = true;
    this.syncSearchRequestFromForm();
    this.resetPaging();
    this.writeUrl();
    this.refreshGrid();
  }

  private syncSearchRequestFromForm() {
    const formValues = this.filterForm.value;

    delete this.searchRequest['keyword'];
    delete this.searchRequest['advancedFilter'];
    delete this.searchRequest['hasNoSchedulerItems'];
    delete this.searchRequest['assignmentTypeIds'];
    delete this.searchRequest['executorIds'];
    delete this.searchRequest['includeUnassignedExecutors'];

    if (formValues.keyword) {
      this.searchRequest['keyword'] = formValues.keyword;
    }
    if (formValues.contact?.id) {
      this.searchRequest['advancedFilter'] = {
        logic: 'and',
        filters: [{ field: 'client.id', operator: 'eq', value: formValues.contact.id } as IFilter]
      };
    }
    if (formValues.status) {
      this.searchRequest.taskStatuses = formValues.status;
    }
    if (formValues.hasNoSchedulerItems) {
      this.searchRequest['hasNoSchedulerItems'] = formValues.hasNoSchedulerItems;
    }
    if (formValues.assignmentTypeIds?.length > 0) {
      this.searchRequest.assignmentTypeIds = formValues.assignmentTypeIds;
    }

    // Executors — split the synthetic "unassigned" sentinel out of the list
    const selected: string[] = formValues.executorIds ?? [];
    const realIds = selected.filter(id => id !== UNASSIGNED_SENTINEL);
    if (realIds.length > 0) {
      this.searchRequest.executorIds = realIds;
    }
    if (selected.includes(UNASSIGNED_SENTINEL)) {
      this.searchRequest.includeUnassignedExecutors = true;
    }
  }

  private buildQueryParamsFromForm(): Params {
    const f = this.filterForm.value;
    const qp: Params = {};
    if (f.keyword) qp.keyword = f.keyword;
    if (f.contact?.id) qp.contactId = f.contact.id;
    if (f.status?.length) qp.status = f.status.join(',');
    if (f.hasNoSchedulerItems) qp.hasNoSchedulerItems = '1';
    if (f.assignmentTypeIds?.length) qp.assignmentTypeIds = f.assignmentTypeIds.join(',');
    if (f.executorIds?.length) qp.executorIds = f.executorIds.join(',');
    return qp;
  }

  /**
   * Writes filter, paging and sort to the URL and remembers them for the
   * detail view's back navigation. buildQueryParamsFromForm() stays
   * paging-free on purpose: it is also used to compare saved filters.
   */
  private writeUrl() {
    const params: Params = {
      ...this.buildQueryParamsFromForm(),
      ...buildPagingQueryParams(this.gridState),
      ...buildSortQueryParams(this.sort, ASSIGNMENTS_DEFAULT_SORT),
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      replaceUrl: true
    });
    this.listQueryState.remember('assignments', params);
  }

  /**
   * Patches the filter form from the URL. The searchRequest itself is built
   * from the URL in ngOnInit; only filter keys count (not page/sort).
   */
  private hydrateFilterFromUrl(): boolean {
    const q = this.route.snapshot.queryParamMap;
    if (!hasAssignmentFilterParams(q)) return false;

    const patch: any = {};
    if (q.has('keyword')) patch.keyword = q.get('keyword');
    if (q.has('contactId')) {
      // Lazily hydrate the full contact object so the combobox template can render name/company.
      this.contactService.getContact(q.get('contactId')!)
        .pipe(first(), untilDestroyed(this))
        .subscribe(contact => this.filterForm.patchValue({ contact }));
    }
    if (q.has('status')) patch.status = q.get('status')!.split(',').map(n => +n);
    if (q.has('hasNoSchedulerItems')) patch.hasNoSchedulerItems = q.get('hasNoSchedulerItems') === '1';
    if (q.has('assignmentTypeIds')) patch.assignmentTypeIds = q.get('assignmentTypeIds')!.split(',');
    if (q.has('executorIds')) patch.executorIds = q.get('executorIds')!.split(',');

    this.filterForm.patchValue(patch);
    return true;
  }

  onCreateNew(){
    this.assignmentStore.resetStore();
    this.router.navigate(['assignment'])
  }

  dueDateClass = dueDateClass;

  onReset(){
    this.filterForm.reset();
    this.filterForm.patchValue({
      status: [TaskStatues.open, TaskStatues.inProgress, TaskStatues.review],
      assignmentTypeIds: [],
      executorIds: [],
    })
    this.showResetButton = false;
    delete this.searchRequest['keyword'];
    delete this.searchRequest['advancedFilter'];
    delete this.searchRequest['hasNoSchedulerItems'];
    delete this.searchRequest['assignmentTypeIds'];
    delete this.searchRequest['executorIds'];
    delete this.searchRequest['includeUnassignedExecutors'];
    this.searchRequest.taskStatuses = [TaskStatues.open, TaskStatues.inProgress, TaskStatues.review]
    this.resetPaging();
    this.writeUrl();
    this.refreshGrid();
  }

}
